import { useEffect, useMemo, useState } from 'react';

// Shared date-range control for the billing overview and per-practice
// insights pages. Each caller passes its own sessionStorage key so the two
// pages remember their last-picked range independently.

export const RANGE_OPTIONS = ['7D', '30D', 'QTD', 'YTD', '12M', 'All'] as const;
export type RangeKey = (typeof RANGE_OPTIONS)[number];

// Calendar months, offered on Home only: monitoring keys its periods by RangeKey.
const MONTH_RANGE_OPTIONS = ['This month', 'Last month'] as const;
type MonthRangeKey = (typeof MONTH_RANGE_OPTIONS)[number];
export const HOME_RANGE_OPTIONS = [...RANGE_OPTIONS, ...MONTH_RANGE_OPTIONS] as const;
export type HomeRangeKey = (typeof HOME_RANGE_OPTIONS)[number];

const DAY_MS = 24 * 60 * 60 * 1000;

function loadRange<K extends HomeRangeKey>(
    storageKey: string,
    options: readonly K[],
    fallback: K
): K {
    try {
        const raw = sessionStorage.getItem(storageKey);
        return options.find((option) => option === raw) ?? fallback;
    } catch {
        return fallback;
    }
}

// The backend filters on the UTC date of each bound; noon UTC lands on the same
// calendar day there and in local display, in any timezone within ±11h.
function calendarDay(year: number, monthIndex: number, day: number): Date {
    return new Date(Date.UTC(year, monthIndex, day, 12));
}

export function monthRangeBounds(key: MonthRangeKey, now: Date): { from: Date; to: Date } {
    const year = now.getFullYear();
    const month = now.getMonth();
    if (key === 'This month') {
        return { from: calendarDay(year, month, 1), to: calendarDay(year, month, now.getDate()) };
    }
    return { from: calendarDay(year, month - 1, 1), to: calendarDay(year, month, 0) };
}

// from = null means all time; the backend anchors it to the earliest data.
export function rangeBounds(key: HomeRangeKey): { from: Date | null; to: Date } {
    const to = new Date();
    switch (key) {
        case 'This month':
        case 'Last month':
            return monthRangeBounds(key, to);
        case '7D':
            return { from: new Date(to.getTime() - 7 * DAY_MS), to };
        case '30D':
            return { from: new Date(to.getTime() - 30 * DAY_MS), to };
        case 'QTD':
            return { from: new Date(to.getFullYear(), Math.floor(to.getMonth() / 3) * 3, 1), to };
        case 'YTD':
            return { from: new Date(to.getFullYear(), 0, 1), to };
        case 'All':
            return { from: null, to };
        default: // 12M: from the start of the month 11 months back, for clean month buckets
            return { from: new Date(to.getFullYear(), to.getMonth() - 11, 1), to };
    }
}

export function fmtRangeDate(date: Date): string {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function useBillingRange<K extends HomeRangeKey>(
    storageKey: string,
    options: readonly K[],
    fallback: K
) {
    const [range, setRange] = useState<K>(() => loadRange(storageKey, options, fallback));

    useEffect(() => {
        try {
            sessionStorage.setItem(storageKey, range);
        } catch {
            /* ignore */
        }
    }, [range, storageKey]);

    const rangeLabel = useMemo(() => {
        const { from, to } = rangeBounds(range);
        return from ? `${fmtRangeDate(from)} - ${fmtRangeDate(to)}` : 'All time';
    }, [range]);

    return { range, setRange, rangeLabel };
}
